import { Component, OnInit, OnDestroy, signal, computed, inject, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Observable, Subject, map } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { EnumValues, BreadcrumbService } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import ObjectId from 'bson-objectid';
import { EnumService } from '../../../services/enum.service';
import { RemissionGuideProxyService } from '../../../proxies/remission-guide.proxy.service';
import {
  RemissionGuideDto,
  RemissionGuideLineDto,
  RemissionGuideStatus,
  RemissionGuideTransportInfoDto,
} from '../../../models/remission-guide/remission-guide';
import { UnitOfMeasure } from '../../../models/inventory/inventory-adjustment';
import { Address, Business, BusinessEntityType } from '../../../models/finance/business';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { ItemMaster, ItemSearchCommand } from '../../../models/item/item-master';
import { InventoryReportProxyService } from '../../../proxies/inventory/inventory-report.proxy.service';

// Strict form interfaces
interface RemissionGuideLineForm {
  id: FormControl<string>;
  itemCode: FormControl<string | null>;
  description: FormControl<string | null>;
  quantity: FormControl<number | null>;
  uom: FormControl<number | null>;
  unitPriceUSD: FormControl<number | null>;
  grossWeightKg: FormControl<number | null>;
  packageCount: FormControl<number | null>;
}

interface TransportInfoForm {
  carrierName: FormControl<string | null>;
  carrierTaxId: FormControl<string | null>;
  driverName: FormControl<string | null>;
  driverTaxId: FormControl<string | null>;
  vehicleId: FormControl<string | null>;
  driverLicenseNumber: FormControl<string | null>;
  tractorId: FormControl<string | null>;
  sealNumber: FormControl<string | null>;
  departureTime: FormControl<string | null>;
  authorizedByUsername: FormControl<string | null>;
}

interface RemissionGuideForm {
  status: FormControl<number | null>;
  issuedDate: FormControl<Date | null>;
  remitterReference: FormControl<string | null>;
  remitterName: FormControl<string | null>;
  consigneeReference: FormControl<string | null>;
  consigneeName: FormControl<string | null>;
  departurePlaceCode: FormControl<string | null>;
  destinationPlaceCode: FormControl<string | null>;
  transferStartDate: FormControl<Date | null>;
  transferEndDate: FormControl<Date | null>;
  remissionGuideTransferTypeCode: FormControl<string | null>;
  otherTransferTypeDetail: FormControl<string | null>;
  footerMessage: FormControl<string | null>;
  lines: FormArray<FormGroup<RemissionGuideLineForm>>;
  transportInfo: FormGroup<TransportInfoForm>;
}

@Component({
  selector: 'app-remission-guide-details',
  templateUrl: './remission-guide-details.component.html',
  styleUrls: ['./remission-guide-details.component.scss'],
})
export class RemissionGuideDetailsComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly enumService = inject(EnumService);
  private readonly translate = inject(TranslateService);
  private readonly proxyService = inject(RemissionGuideProxyService);
  private readonly businessProxyService = inject(BusinessProxyService);
  private readonly itemMasterProxyService = inject(ItemMasterProxyService);
  private readonly reportProxyService = inject(InventoryReportProxyService);

  // State
  isLoading = signal(false);
  isDownloading = signal(false);
  isNewRecord = signal(true);
  remissionGuide = signal<RemissionGuideDto | null>(null);

  // Business autocomplete
  selectedRemitter = signal<Business | null>(null);
  selectedConsignee = signal<Business | null>(null);

  // Item code autocomplete
  selectedItemForLine = signal<ItemMaster | null>(null);

  // Address options for departure/destination dropdowns
  remitterAddressOptions = signal<{ label: string; value: string }[]>([]);
  consigneeAddressOptions = signal<{ label: string; value: string }[]>([]);

  // UOM options for dropdown
  readonly uomOptions = Object.keys(UnitOfMeasure)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ({ label: key, value: UnitOfMeasure[key as keyof typeof UnitOfMeasure] }));

  // Status options for dropdown
  statusSelectOptions = computed(() => {
    const statuses = this.remissionGuideStatus();
    if (Object.keys(statuses).length > 0) {
      return Object.keys(statuses)
        .filter((key) => !isNaN(Number(key)))
        .map((key) => ({ label: statuses[Number(key)], value: Number(key) }));
    }
    return [
      { label: 'Draft', value: RemissionGuideStatus.Draft },
      { label: 'Delivered', value: RemissionGuideStatus.Delivered },
    ];
  });

  // Breadcrumb
  private readonly syncBreadcrumb = effect(
    () => {
      this.breadcrumbService.setCurrent(this.isNewRecord() ? 'Common_New' : this.remissionGuide()?.number);
    },
    { allowSignalWrites: true },
  );

  // Enums
  remissionGuideStatus = signal<EnumValues>({});

  // Computed
  readonly isDraft = computed(() => {
    const guide = this.remissionGuide();
    return !guide || guide.status === RemissionGuideStatus.Draft;
  });

  readonly canEdit = computed(() => {
    return this.isDraft() && (AuthService.hasRole(UserGroup.RemissionGuideEditor) || AuthService.hasRole(UserGroup.RemissionGuideAdmin));
  });

  readonly canDelete = computed(() => {
    return this.isDraft() && !this.isNewRecord() && AuthService.hasRole(UserGroup.RemissionGuideAdmin);
  });

  // Main Form
  mainForm = new FormGroup<RemissionGuideForm>({
    status: new FormControl<number | null>(RemissionGuideStatus.Draft),
    issuedDate: new FormControl<Date | null>(null, [Validators.required]),
    remitterReference: new FormControl<string | null>(null, [Validators.required]),
    remitterName: new FormControl<string | null>(null),
    consigneeReference: new FormControl<string | null>(null, [Validators.required]),
    consigneeName: new FormControl<string | null>(null),
    departurePlaceCode: new FormControl<string | null>(null, [Validators.required]),
    destinationPlaceCode: new FormControl<string | null>(null, [Validators.required]),
    transferStartDate: new FormControl<Date | null>(null, [Validators.required]),
    transferEndDate: new FormControl<Date | null>(null, [Validators.required]),
    remissionGuideTransferTypeCode: new FormControl<string | null>(null, [Validators.required]),
    otherTransferTypeDetail: new FormControl<string | null>(null),
    footerMessage: new FormControl<string | null>(null),
    lines: new FormArray<FormGroup<RemissionGuideLineForm>>([]),
    transportInfo: new FormGroup<TransportInfoForm>({
      carrierName: new FormControl<string | null>(null),
      carrierTaxId: new FormControl<string | null>(null),
      driverName: new FormControl<string | null>(null),
      driverTaxId: new FormControl<string | null>(null),
      vehicleId: new FormControl<string | null>(null),
      driverLicenseNumber: new FormControl<string | null>(null),
      tractorId: new FormControl<string | null>(null),
      sealNumber: new FormControl<string | null>(null),
      departureTime: new FormControl<string | null>(null),
      authorizedByUsername: new FormControl<string | null>(null),
    }),
  });

  get linesFormArray(): FormArray<FormGroup<RemissionGuideLineForm>> {
    return this.mainForm.controls.lines;
  }

  ngOnInit(): void {
    // Load enums
    const statusValues = this.enumService.getEnum('RemissionGuideStatus', 'inventory').values;
    if (Object.keys(statusValues).length > 0) {
      this.remissionGuideStatus.set(statusValues);
    }

    // Check for existing record
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params['id'];
      if (id) {
        this.isNewRecord.set(false);
        this.loadRemissionGuide(id);
      } else {
        this.isNewRecord.set(true);
        this.addLine(); // Start with one empty line
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadRemissionGuide(id: string): void {
    this.isLoading.set(true);
    this.proxyService
      .getById(id)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (result) => {
          const guide = result?.data;
          if (guide) {
            this.remissionGuide.set(guide);
            this.populateForm(guide);
          } else {
            GlobalMessageUtil.error('Error', 'Remission guide not found');
            this.router.navigate(['/remission-guides']);
          }
        },
        error: () => {
          this.router.navigate(['/remission-guides']);
        },
      });
  }

  private populateForm(guide: RemissionGuideDto): void {
    this.mainForm.patchValue({
      status: guide.status,
      issuedDate: guide.issuedDate ? new Date(guide.issuedDate) : null,
      remitterReference: guide.remitter?.reference || null,
      remitterName: guide.remitter?.name || null,
      consigneeReference: guide.consignee?.reference || null,
      consigneeName: guide.consignee?.name || null,
      departurePlaceCode: guide.departurePlaceCode,
      destinationPlaceCode: guide.destinationPlaceCode,
      transferStartDate: guide.transferStartDate ? new Date(guide.transferStartDate) : null,
      transferEndDate: guide.transferEndDate ? new Date(guide.transferEndDate) : null,
      remissionGuideTransferTypeCode: guide.remissionGuideTransferTypeCode,
      otherTransferTypeDetail: guide.otherTransferTypeDetail,
      footerMessage: guide.footerMessage,
    });

    // Populate transport info
    if (guide.transportInfo) {
      this.mainForm.controls.transportInfo.patchValue(guide.transportInfo);
    }

    // Populate lines
    this.linesFormArray.clear();
    for (const line of guide.lines) {
      this.addLine(line);
    }

    // Set placeholder business objects immediately so autocompletes show the name
    if (guide.remitter?.name) {
      this.selectedRemitter.set({ name: guide.remitter.name, id: guide.remitter.reference } as Business);
    }
    if (guide.consignee?.name) {
      this.selectedConsignee.set({ name: guide.consignee.name, id: guide.consignee.reference } as Business);
    }

    // Fetch full business entities in a single call to populate address dropdowns
    const businessIds = [guide.remitter?.reference, guide.consignee?.reference].filter((id): id is string => !!id);
    if (businessIds.length > 0) {
      this.businessProxyService.getByIds(businessIds).subscribe({
        next: (result) => {
          const businesses = result?.data || [];
          const remitterRef = guide.remitter?.reference;
          const consigneeRef = guide.consignee?.reference;

          for (const business of businesses) {
            if (business.id === remitterRef) {
              this.selectedRemitter.set(business);
              this.remitterAddressOptions.set(this.mapAddressOptions(business.addresses));
            }
            if (business.id === consigneeRef) {
              this.selectedConsignee.set(business);
              this.consigneeAddressOptions.set(this.mapAddressOptions(business.addresses));
            }
          }
        },
        error: () => {
          console.error('Failed to load business entities:', businessIds);
        },
      });
    }

    // Disable form if not draft
    if (guide.status !== RemissionGuideStatus.Draft) {
      this.mainForm.disable();
    }
  }

  // Lines management
  addLine(lineData?: RemissionGuideLineDto): void {
    const lineGroup = new FormGroup<RemissionGuideLineForm>({
      id: new FormControl<string>(lineData?.id || this.generateTempId(), { nonNullable: true }),
      itemCode: new FormControl<string | null>(lineData?.itemCode || null),
      description: new FormControl<string | null>(lineData?.description || null),
      quantity: new FormControl<number | null>(lineData?.quantity || null, [Validators.required, Validators.min(0.01)]),
      uom: new FormControl<number | null>(lineData?.uom ?? null),
      unitPriceUSD: new FormControl<number | null>(lineData?.totalPrice || null),
      grossWeightKg: new FormControl<number | null>(lineData?.grossWeightKg || null),
      packageCount: new FormControl<number | null>(lineData?.packageCount ?? null),
    });
    this.linesFormArray.push(lineGroup);
  }

  removeLine(index: number): void {
    this.linesFormArray.removeAt(index);
  }

  private generateTempId(): string {
    return new ObjectId().toHexString();
  }

  // Business autocomplete
  public fetchRemitters = (query: string): Observable<Business[]> =>
    this.businessProxyService
      .search({ name: query, businessTypes: [BusinessEntityType.InternalCompany] })
      .pipe(map((result) => result?.data?.items || []));

  public fetchConsignees = (query: string): Observable<Business[]> =>
    this.businessProxyService
      .search({ name: query, businessTypes: [BusinessEntityType.Client, BusinessEntityType.Consignee] })
      .pipe(map((result) => result?.data?.items || []));

  onRemitterSelect(business: Business): void {
    this.selectedRemitter.set(business);
    this.mainForm.controls.remitterReference.setValue(business.id || null);
    this.mainForm.controls.remitterName.setValue(business.name || null);
    const options = this.mapAddressOptions(business.addresses);
    this.remitterAddressOptions.set(options);
    if (options.length === 1) {
      this.mainForm.controls.departurePlaceCode.setValue(options[0].value);
    } else {
      this.mainForm.controls.departurePlaceCode.setValue(null);
    }
  }

  onConsigneeSelect(business: Business): void {
    this.selectedConsignee.set(business);
    this.mainForm.controls.consigneeReference.setValue(business.id || null);
    this.mainForm.controls.consigneeName.setValue(business.name || null);
    const options = this.mapAddressOptions(business.addresses);
    this.consigneeAddressOptions.set(options);
    if (options.length === 1) {
      this.mainForm.controls.destinationPlaceCode.setValue(options[0].value);
    } else {
      this.mainForm.controls.destinationPlaceCode.setValue(null);
    }
  }

  onRemitterClear(): void {
    this.selectedRemitter.set(null);
    this.mainForm.controls.remitterReference.setValue(null);
    this.mainForm.controls.remitterName.setValue(null);
    this.mainForm.controls.departurePlaceCode.setValue(null);
    this.remitterAddressOptions.set([]);
  }

  onConsigneeClear(): void {
    this.selectedConsignee.set(null);
    this.mainForm.controls.consigneeReference.setValue(null);
    this.mainForm.controls.consigneeName.setValue(null);
    this.mainForm.controls.destinationPlaceCode.setValue(null);
    this.consigneeAddressOptions.set([]);
  }

  private mapAddressOptions(addresses?: Address[]): { label: string; value: string }[] {
    if (!addresses || addresses.length === 0) {
      return [];
    }
    return addresses.map((addr) => ({
      label: this.formatAddressLabel(addr),
      value: addr.code.toString(),
    }));
  }

  private formatAddressLabel(addr: Address): string {
    if (addr.displayName) {
      return addr.displayName;
    }
    const parts = [addr.address1, addr.city, addr.stateOrProvince, addr.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : `Address ${addr.code}`;
  }

  // Item code autocomplete
  public fetchItems = (query: string): Observable<ItemMaster[]> => {
    const command: ItemSearchCommand = { itemCode: query, name: query };

    return this.itemMasterProxyService.search(command, true).pipe(map((result) => result?.data?.items || []));
  };

  onItemSelect(item: ItemMaster): void {
    const lang = this.translate.currentLang || 'es';
    const description = item.itemDescription?.[lang as keyof typeof item.itemDescription] || item.itemLongDisplayName || item.itemCode;
    this.addLineFromItem(item.itemCode, description as string, item.unitOfMeasure);
    this.selectedItemForLine.set(null);
  }

  addLineFromItem(itemCode: string, description: string, uom?: number): void {
    const lineGroup = new FormGroup<RemissionGuideLineForm>({
      id: new FormControl<string>(this.generateTempId(), { nonNullable: true }),
      itemCode: new FormControl<string | null>(itemCode),
      description: new FormControl<string | null>(description),
      quantity: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
      uom: new FormControl<number | null>(uom ?? null),
      unitPriceUSD: new FormControl<number | null>(null),
      grossWeightKg: new FormControl<number | null>(null),
      packageCount: new FormControl<number | null>(null),
    });
    this.linesFormArray.push(lineGroup);
  }

  getItemDisplayLabel(item: ItemMaster): string {
    const lang = this.translate.currentLang || 'es';
    const name = item.itemDisplayName?.[lang as keyof typeof item.itemDisplayName] || item.itemLongDisplayName || '';
    return `${item.itemCode} - ${name}`;
  }

  isLineFromCatalog(lineControl: FormGroup<RemissionGuideLineForm>): boolean {
    return !!lineControl.controls.itemCode.value;
  }

  getUomLabel(value: number | null): string {
    if (value == null) return '-';
    const option = this.uomOptions.find((o) => o.value === value);
    return option?.label ?? '-';
  }

  calculateUnitPrice(lineControl: FormGroup<RemissionGuideLineForm>): number {
    const totalPrice = lineControl.controls.unitPriceUSD.value ?? 0;
    const quantity = lineControl.controls.quantity.value ?? 0;
    return quantity > 0 ? totalPrice / quantity : 0;
  }

  // Save / Update
  onSave(): void {
    if (!this.mainForm.valid) {
      this.mainForm.markAllAsTouched();
      GlobalMessageUtil.warn('Validation', 'Please fill in all required fields');
      return;
    }

    // Validate lines - at least one line with itemCode or description
    const lines = this.linesFormArray.value;
    if (lines.length === 0) {
      GlobalMessageUtil.warn('Validation', 'At least one line is required');
      return;
    }

    for (const line of lines) {
      if (!line.itemCode?.trim() && !line.description?.trim()) {
        GlobalMessageUtil.warn('Validation', 'Each line must have an Item Code or Description');
        return;
      }
    }

    const formValue = this.mainForm.getRawValue();
    const existingGuide = this.remissionGuide();
    const toUndef = <T>(val: T | null): T | undefined => (val === null ? undefined : val);

    const body: Partial<RemissionGuideDto> = {
      ...(existingGuide ? { id: existingGuide.id, number: existingGuide.number } : {}),
      status: formValue.status ?? undefined,
      issuedDate: formValue.issuedDate?.toISOString(),
      remitterReference: formValue.remitterReference ?? undefined,
      remitter: { reference: toUndef(formValue.remitterReference), name: toUndef(formValue.remitterName) },
      consigneeReference: formValue.consigneeReference ?? undefined,
      consignee: { reference: toUndef(formValue.consigneeReference), name: toUndef(formValue.consigneeName) },
      departurePlaceCode: formValue.departurePlaceCode ?? undefined,
      destinationPlaceCode: formValue.destinationPlaceCode ?? undefined,
      transferStartDate: formValue.transferStartDate?.toISOString(),
      transferEndDate: formValue.transferEndDate?.toISOString(),
      remissionGuideTransferTypeCode: formValue.remissionGuideTransferTypeCode ?? undefined,
      otherTransferTypeDetail: toUndef(formValue.otherTransferTypeDetail),
      footerMessage: toUndef(formValue.footerMessage),
      lines: formValue.lines.map((l) => ({
        id: l.id,
        itemCode: l.itemCode || undefined,
        description: l.description || undefined,
        quantity: l.quantity!,
        uom: l.uom ?? 0,
        totalPrice: l.unitPriceUSD ?? 0,
        grossWeightKg: l.grossWeightKg ?? 0,
        netWeightKg: l.grossWeightKg ?? 0,
        packageCount: l.packageCount ?? 0,
      })),
      transportInfo: toUndef(formValue.transportInfo) as RemissionGuideTransportInfoDto | undefined,
    };

    this.isLoading.set(true);

    const operation = this.isNewRecord() ? this.proxyService.create(body) : this.proxyService.update(existingGuide!.id, body);

    operation.pipe(finalize(() => this.isLoading.set(false))).subscribe({
      next: (result) => {
        GlobalMessageUtil.success('Success', this.isNewRecord() ? 'Remission guide created' : 'Remission guide updated');
        if (this.isNewRecord() && result?.data?.id) {
          this.router.navigate(['/remission-guide-details'], { queryParams: { id: result.data.id } });
        } else {
          this.loadRemissionGuide(this.remissionGuide()!.id);
        }
      },
    });
  }

  // Delete
  onDelete(): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this remission guide?',
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.isLoading.set(true);
        this.proxyService
          .delete(this.remissionGuide()!.id)
          .pipe(finalize(() => this.isLoading.set(false)))
          .subscribe({
            next: () => {
              GlobalMessageUtil.success('Success', 'Remission guide deleted');
              this.router.navigate(['/remission-guides']);
            },
          });
      },
    });
  }

  onCancel(): void {
    this.router.navigate(['/remission-guides']);
  }

  printReport(): void {
    const guide = this.remissionGuide();
    if (!guide) return;

    this.isDownloading.set(true);
    this.reportProxyService
      .generateRemissionGuideReport(guide.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isDownloading.set(false)),
      )
      .subscribe({
        next: (blob) => {
          const fileName = `RemissionGuide_${guide.number}_${new Date().getTime()}.pdf`;
          this.downloadBlob(blob, fileName);
          GlobalMessageUtil.success('Success', 'Report downloaded');
        },
      });
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  getStatusLabel(status: RemissionGuideStatus): string {
    const statuses = this.remissionGuideStatus();
    return statuses[status] || status.toString();
  }

  getStatusSeverity(status: RemissionGuideStatus): 'success' | 'info' | 'warn' | 'secondary' | 'contrast' | 'danger' | undefined {
    switch (status) {
      case RemissionGuideStatus.Draft:
        return 'secondary';
      case RemissionGuideStatus.Delivered:
        return 'success';
      default:
        return 'secondary';
    }
  }
}
